import React, { useState } from 'react';

import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import type { BaseViewPageProps } from '@confluence/view-page-common/entry-points/BaseViewPageProps.ts';
import { useViewPagePersistentScrollPosition } from '@confluence/view-page-common/entry-points/useViewPagePersistentScrollPosition.ts';
import { StartViewPageLoad } from '@confluence/view-page-common/entry-points/perf.config.ts';
import { ViewPageCommon } from '@confluence/view-page-common/entry-points/ViewPageCommon.ts';
import { useEventHandlers } from '@confluence/adf-renderer/entry-points/useEventHandlers.ts';

import { useTrackingConfig } from '../embedded-common/useTrackingConfig.ts';
import { usePageTheme } from '../embedded-common/usePageTheme.ts';

import { HeaderContainer } from './HeaderContainer.tsx';
import { expValEquals } from '@confluence/feature-experiments';

// In embedded view, the renderer and title components own their own 72px gutters,
// so the outer container padding is zeroed out.
// The cover-picture CSS variables are also neutralised (to 0px / 100%) so that
// the cover image remains edge-to-edge against a zero-padded container.
const EMBEDDED_CONTENT_SCREEN_STYLES = {
	paddingTop: '0px',
	paddingBottom: '0px',
	paddingLeft: '0px',
	paddingRight: '0px',
	'--full-cover-image-margin': '0px',
	'--full-cover-image-width': '100%',
};

type EmbeddedViewPageProps = BaseViewPageProps & {
	classicComments?: React.ComponentType<any>;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	showHeaderButtons?: boolean;
};

export const EmbeddedViewPage: React.FC<EmbeddedViewPageProps> = ({
	classicComments,
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	showHeaderButtons = true, // for backwards compatibility
}) => {
	usePageTheme();
	const {
		hasByLineContributors,
		hasByLineExtensions,
		hasComments,
		hasInlineCommentsAndHighlightActions,
		hasPageReactions,
		hasStickyHeader,
		showDelete,
		showEdit,
		showShare,
		includeNonLicensedUsersToShare,
	} = useEmbeddedViewPageFeatures();
	const customTrackingConfig = useTrackingConfig();

	const eventHandlers = useEventHandlers();

	useViewPagePersistentScrollPosition(contentId, 'EP');

	const [hasCoverPicture, setHasCoverPicture] = useState(false);

	return (
		<>
			<StartViewPageLoad contentId={contentId} customConfig={customTrackingConfig} />
			<ViewPageCommon
				classicComments={classicComments}
				contentId={contentId}
				contentScreenStyles={EMBEDDED_CONTENT_SCREEN_STYLES}
				eventHandlers={eventHandlers}
				hasSatisfactionSurveys={false}
				hasEOPRecs={false}
				hasInlineComments={hasInlineCommentsAndHighlightActions}
				hasLabels={false}
				hasReactions={hasPageReactions}
				hasPageComments={hasComments}
				hasInlineActions={hasInlineCommentsAndHighlightActions}
				spaceKey={spaceKey}
				setHasCoverPicture={setHasCoverPicture}
				isEmbeddedPage
				titleAndByLineOptions={{
					hasByLineContributors,
					hasByLineExtensions,
					headingContainer: !expValEquals('cc-maui-experiment', 'isEnabled', true)
						? ({ children, showInlineCommentButton }) => (
								<HeaderContainer
									contentId={contentId}
									showInlineCommentButton={showHeaderButtons && showInlineCommentButton}
									hasInlineComments={hasInlineCommentsAndHighlightActions}
									hasStickyHeader={hasStickyHeader}
									spaceKey={spaceKey}
									showDelete={showHeaderButtons && showDelete}
									showEdit={showHeaderButtons && showEdit}
									showShare={showHeaderButtons && showShare}
									includeNonLicensedUsersToShare={includeNonLicensedUsersToShare}
									hasCoverPicture={hasCoverPicture}
								>
									{children}
								</HeaderContainer>
							)
						: undefined,
				}}
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
			/>
		</>
	);
};
